import {
  BUDGET_PERIOD_STATUS_LABELS,
  BUDGET_TYPE_LABELS,
  BUDGET_TYPES,
  budgetItemInputSchema,
  budgetItemSchema,
  budgetListSchema,
  budgetOptionsSchema,
  budgetValueSchema,
  formatDate,
  formatDecimal,
  type BudgetItem,
  type BudgetList,
  type BudgetOptions,
  type BudgetTypeName,
} from '@fluxus/shared';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, Eye, FolderKanban, Pencil, Plus, Wallet } from 'lucide-react';
import { Fragment, useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';
import { moneyInput } from '@/lib/money';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const money = (v: number | null) => (v === null ? '—' : formatDecimal(v, 2));
const pct = (v: number | null) => (v === null ? '—' : `${formatDecimal(v, 2)}%`);
const inputText = (v: number | null | undefined) =>
  v === null || v === undefined ? '' : v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const textareaClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring aria-invalid:border-danger';

/**
 * Planejamento › Orçamento (T-182b, RF-30, PRD §12; DEC-065): itens com categoria, Opex/Capex, vigência e vínculo; a
 * grade mensal de planejado e realizado, os totais por categoria e tipo e o detalhamento de cada item.
 */
export function OrcamentoPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer className="max-w-6xl">
      <PageHeader
        title="Orçamento"
        description="Planejado e realizado mês a mês por item, com % consumido, saldos, totais por categoria e Opex/Capex e a vigência de cada item."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="O orçamento pertence a um projeto. Quando a empresa tiver um projeto em andamento, ele aparece aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="mt-4">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
          </div>
          <BudgetSection
            key={project.id}
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </>
      )}
    </PageContainer>
  );
}

function BudgetSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can = (action: 'create' | 'edit' | 'delete') => !readOnly && canDo('previsto-realizado', action);
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/budget`;
  const [params, setParams] = useSearchParams();
  const [year, setYear] = useState<number | null>(null);
  const [archived, setArchived] = useState(false);
  const [editing, setEditing] = useState<BudgetItem | 'new' | null>(null);
  const ids = { year: useId() };
  const list = useQuery({
    queryKey: ['budget', projectId, year, archived],
    queryFn: ({ signal }) =>
      client.request(base, {
        query: { ...(year ? { year } : {}), ...(archived ? { archived: 'true' } : {}) },
        schema: budgetListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const options = useQuery({
    queryKey: ['budget', projectId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: budgetOptionsSchema, signal }),
    enabled: can('create') || can('edit'),
  });
  const data = list.data;
  const chosenYear = year ?? data?.year ?? new Date().getFullYear();
  const openId = params.get('item');
  const openItem = (id: string | null) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (id) next.set('item', id);
        else next.delete('item');
        return next;
      },
      { replace: true },
    );
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['budget', projectId] });

  const [archiving, setArchiving] = useState(false);
  async function toggleArchive(item: BudgetItem) {
    if (archiving) return;
    const verb = item.archivedAt ? 'restore' : 'archive';
    setArchiving(true);
    try {
      await client.request(`${base}/${item.id}/${verb}`, { method: 'POST', schema: budgetItemSchema });
      toast.success(verb === 'archive' ? 'Item arquivado' : 'Item restaurado', { description: item.name });
      await refresh();
    } catch (err) {
      toast.error('Não foi possível concluir', { description: errorMessage(err) });
    } finally {
      setArchiving(false);
    }
  }

  return (
    <div className="mt-6 flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <FormField id={ids.year} label="Ano">
            <NativeSelect id={ids.year} value={chosenYear} onChange={(e) => setYear(Number(e.target.value))}>
              {(data?.years ?? [chosenYear]).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-brand"
              checked={archived}
              onChange={(e) => setArchived(e.target.checked)}
            />
            Ver arquivados
          </label>
        </div>
        {can('create') ? (
          <Button onClick={() => setEditing('new')}>
            <Plus aria-hidden />
            Novo item
          </Button>
        ) : null}
      </div>

      {list.isPending ? (
        <LoadingState rows={4} label="Carregando o orçamento" />
      ) : list.isError && !data ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : !data || !data.items.length ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-12"
          icon={Wallet}
          title={archived ? 'Nenhum item arquivado neste ano' : 'Nenhum item do orçamento neste ano'}
          description="Cadastre os itens com a categoria, Opex ou Capex e a vigência; depois lance o planejado e o realizado de cada mês."
        />
      ) : (
        <>
          {list.isError ? <FormAlert tone="error">{errorMessage(list.error)}</FormAlert> : null}
          <Summary data={data} />
          <Grid data={data} onOpen={openItem} />
          <Totals data={data} />
        </>
      )}

      <ItemDialog
        base={base}
        projectId={projectId}
        itemId={openId}
        year={chosenYear}
        canEdit={can('edit')}
        canArchive={can('delete')}
        archiving={archiving}
        onClose={() => openItem(null)}
        onEdit={(item) => setEditing(item)}
        onArchive={toggleArchive}
        onChanged={refresh}
      />
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        title={editing === 'new' ? 'Novo item do orçamento' : 'Editar item do orçamento'}
        className="w-[min(40rem,calc(100vw-2rem))]"
      >
        {editing !== null ? (
          <ItemForm
            base={base}
            initial={editing === 'new' ? undefined : editing}
            categories={data?.categories ?? []}
            options={options.data}
            optionsError={options.isError ? options.error : null}
            onRetryOptions={() => void options.refetch()}
            onCancel={() => setEditing(null)}
            onDone={async (saved) => {
              setEditing(null);
              await refresh();
              openItem(saved.id);
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

function Summary({ data }: { data: BudgetList }) {
  const cards = [
    { label: `Planejado ${data.year}`, value: money(data.annual.planned) },
    { label: 'Realizado', value: money(data.annual.actual) },
    { label: '% consumido', value: pct(data.annual.consumedPercent) },
    { label: 'Saldo disponível', value: money(data.annual.available), negative: data.annual.available < 0 },
  ];
  return (
    <dl aria-label="Resumo do ano" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cards.map((c) => (
        <div key={c.label} className="rounded-lg border border-border p-4">
          <dt className="text-xs font-medium text-muted-foreground">{c.label}</dt>
          <dd className={`mt-1 text-lg font-bold tabular ${c.negative ? 'text-status-red-fg' : ''}`}>
            {c.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Grade mensal: um item por linha, planejado e realizado de cada mês, com o total do ano e o saldo. */
function Grid({ data, onOpen }: { data: BudgetList; onOpen: (id: string) => void }) {
  return (
    <section aria-labelledby="budget-grid-title" className="flex flex-col gap-3">
      <h2 id="budget-grid-title" className="text-lg font-bold">
        Grade mensal de {data.year}
      </h2>
      <ul aria-label="Itens do orçamento" className="grid gap-3 lg:hidden">
        {data.items.map((item) => (
          <li key={item.id}>
            <article
              aria-label={item.name}
              className="flex flex-col gap-2 rounded-lg border border-border p-4 text-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold">{item.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {item.category} · {BUDGET_TYPE_LABELS[item.type]} ·{' '}
                    {BUDGET_PERIOD_STATUS_LABELS[item.period.status]}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onOpen(item.id)}
                  aria-label={`Abrir ${item.name}`}
                >
                  <Eye aria-hidden />
                  Abrir
                </Button>
              </div>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
                <dt className="text-muted-foreground">Planejado</dt>
                <dd className="text-right tabular">{money(item.annual.planned)}</dd>
                <dt className="text-muted-foreground">Realizado</dt>
                <dd className="text-right tabular">{money(item.annual.actual)}</dd>
                <dt className="text-muted-foreground">% consumido</dt>
                <dd className="text-right tabular">{pct(item.annual.consumedPercent)}</dd>
                <dt className="text-muted-foreground">Saldo</dt>
                <dd className="text-right tabular">{money(item.annual.available)}</dd>
              </dl>
            </article>
          </li>
        ))}
      </ul>
      <div
        role="region"
        aria-label="Grade mensal do orçamento"
        tabIndex={0}
        className="relative hidden overflow-x-auto rounded-lg border border-border lg:block"
      >
        <table className="w-full min-w-[80rem] border-collapse text-sm">
          <caption className="sr-only">Planejado e realizado de cada item, mês a mês, em {data.year}</caption>
          <thead className="bg-muted/60 text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="sticky left-0 bg-muted px-3 py-2 text-left font-semibold">
                Item
              </th>
              {MONTHS.map((m) => (
                <th key={m} scope="col" className="px-2 py-2 text-right font-semibold">
                  {m}
                </th>
              ))}
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Total do ano
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                % consumido
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Saldo
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <tr key={item.id} className="border-t border-border align-top">
                <th scope="row" className="sticky left-0 bg-surface px-3 py-2 text-left font-semibold">
                  <button
                    type="button"
                    onClick={() => onOpen(item.id)}
                    className="min-h-11 text-left underline-offset-2 hover:underline"
                  >
                    {item.name}
                  </button>
                  <span className="block text-xs font-normal text-muted-foreground">
                    {item.category} · {BUDGET_TYPE_LABELS[item.type]}
                  </span>
                </th>
                {item.months.map((m) => (
                  <td key={m.month} className="px-2 py-2 text-right tabular">
                    <span className="block">{money(m.planned)}</span>
                    <span className="block text-xs text-muted-foreground">{money(m.actual)}</span>
                  </td>
                ))}
                <td className="px-3 py-2 text-right tabular">
                  <span className="block font-semibold">{money(item.annual.planned)}</span>
                  <span className="block text-xs text-muted-foreground">{money(item.annual.actual)}</span>
                </td>
                <td className="px-3 py-2 text-right tabular">{pct(item.annual.consumedPercent)}</td>
                <td
                  className={`px-3 py-2 text-right tabular ${item.annual.available < 0 ? 'text-status-red-fg' : ''}`}
                >
                  {money(item.annual.available)}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-border bg-muted/40 font-semibold">
              <th scope="row" className="sticky left-0 bg-muted px-3 py-2 text-left">
                Total
              </th>
              {data.months.map((m) => (
                <td key={m.month} className="px-2 py-2 text-right tabular">
                  <span className="block">{money(m.planned)}</span>
                  <span className="block text-xs text-muted-foreground">{money(m.actual)}</span>
                </td>
              ))}
              <td className="px-3 py-2 text-right tabular">
                <span className="block">{money(data.annual.planned)}</span>
                <span className="block text-xs text-muted-foreground">{money(data.annual.actual)}</span>
              </td>
              <td className="px-3 py-2 text-right tabular">{pct(data.annual.consumedPercent)}</td>
              <td className="px-3 py-2 text-right tabular">{money(data.annual.available)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <section
        aria-label={`Total por mês de ${data.year}`}
        className="rounded-lg border border-border p-4 lg:hidden"
      >
        <h3 className="mb-2 text-sm font-semibold">Total por mês</h3>
        <dl className="grid grid-cols-[auto_1fr_1fr] gap-x-3 gap-y-1 text-sm">
          <dt className="sr-only">Mês</dt>
          <dd className="text-xs text-muted-foreground">Mês</dd>
          <dd className="text-right text-xs text-muted-foreground">Planejado</dd>
          <dd className="text-right text-xs text-muted-foreground">Realizado</dd>
          {data.months.map((m) => (
            <Fragment key={m.month}>
              <dt className="font-medium">{MONTHS[m.month - 1]}</dt>
              <dd className="text-right tabular">{money(m.planned)}</dd>
              <dd className="text-right tabular">{money(m.actual)}</dd>
            </Fragment>
          ))}
        </dl>
      </section>
      <p className="hidden text-xs text-muted-foreground lg:block">
        Em cada mês: planejado em cima e realizado embaixo.
      </p>
    </section>
  );
}

function Totals({ data }: { data: BudgetList }) {
  const groups = [
    { title: 'Por tipo', rows: data.totalsByType.map((t) => ({ ...t, label: BUDGET_TYPE_LABELS[t.key] })) },
    { title: 'Por categoria', rows: data.totalsByCategory.map((t) => ({ ...t, label: t.key })) },
  ];
  return (
    <section aria-labelledby="budget-totals-title" className="flex flex-col gap-3">
      <h2 id="budget-totals-title" className="text-lg font-bold">
        Totais de {data.year}
      </h2>
      <div className="grid gap-4 lg:grid-cols-2">
        {groups.map((g) => (
          <div key={g.title} className="relative overflow-x-auto rounded-lg border border-border">
            <table className="w-full border-collapse text-sm">
              <caption className="px-3 pt-3 text-left font-semibold">{g.title}</caption>
              <thead className="text-xs text-muted-foreground">
                <tr>
                  {['', 'Planejado', 'Realizado', '% consumido', 'Saldo'].map((h, i) => (
                    <th
                      key={h || 'nome'}
                      scope="col"
                      className={`px-3 py-2 font-semibold ${i ? 'text-right' : 'text-left'}`}
                    >
                      {h || <span className="sr-only">Grupo</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r) => (
                  <tr key={r.key} className="border-t border-border">
                    <th scope="row" className="px-3 py-2 text-left font-medium">
                      {r.label}
                    </th>
                    <td className="px-3 py-2 text-right tabular">{money(r.planned)}</td>
                    <td className="px-3 py-2 text-right tabular">{money(r.actual)}</td>
                    <td className="px-3 py-2 text-right tabular">{pct(r.consumedPercent)}</td>
                    <td
                      className={`px-3 py-2 text-right tabular ${r.available < 0 ? 'text-status-red-fg' : ''}`}
                    >
                      {money(r.available)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Detalhamento do item (?item=): vigência, vínculo, mês a mês com saldos, totais e a edição dos valores. */
function ItemDialog({
  base,
  projectId,
  itemId,
  year,
  canEdit,
  canArchive,
  archiving,
  onClose,
  onEdit,
  onArchive,
  onChanged,
}: {
  base: string;
  projectId: string;
  itemId: string | null;
  year: number;
  canEdit: boolean;
  canArchive: boolean;
  archiving: boolean;
  onClose: () => void;
  onEdit: (item: BudgetItem) => void;
  onArchive: (item: BudgetItem) => Promise<void>;
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [itemYear, setItemYear] = useState<number | null>(null);
  // O ano pedido; a API devolve o ano mostrado, limitado à vigência do item (item.year).
  const askedYear = itemYear ?? year;
  const query = useQuery({
    queryKey: ['budget', projectId, 'item', itemId, askedYear],
    queryFn: ({ signal }) =>
      client.request(`${base}/${itemId}`, { query: { year: askedYear }, schema: budgetItemSchema, signal }),
    enabled: itemId !== null,
  });
  const item = query.data;
  const yearsOfItem = item
    ? Array.from(
        { length: Number(item.endDate.slice(0, 4)) - Number(item.startDate.slice(0, 4)) + 1 },
        (_, i) => Number(item.startDate.slice(0, 4)) + i,
      )
    : [];
  return (
    <Dialog
      open={itemId !== null}
      onOpenChange={(open) => {
        if (!open) {
          setItemYear(null);
          onClose();
        }
      }}
      title={item?.name ?? 'Item do orçamento'}
      className="w-[min(56rem,calc(100vw-2rem))]"
    >
      {query.isPending ? (
        <LoadingState rows={3} label="Carregando o item" />
      ) : query.isError ? (
        query.error instanceof ApiError && query.error.status === 404 ? (
          <EmptyState
            icon={Wallet}
            title="Item não encontrado"
            description="Ele não existe neste projeto ou está fora do seu escopo."
          />
        ) : (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
        )
      ) : item ? (
        <div className="flex flex-col gap-4 text-sm">
          <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
            <dt className="text-muted-foreground">Categoria e tipo</dt>
            <dd>
              {item.category} · {BUDGET_TYPE_LABELS[item.type]}
            </dd>
            <dt className="text-muted-foreground">Vigência</dt>
            <dd>
              {formatDate(item.startDate)} a {formatDate(item.endDate)} ·{' '}
              {BUDGET_PERIOD_STATUS_LABELS[item.period.status]}
            </dd>
            <dt className="text-muted-foreground">Dias restantes</dt>
            <dd className="tabular">
              {item.period.daysRemaining} de {item.period.totalDays}
            </dd>
            <dt className="text-muted-foreground">Vínculo</dt>
            <dd>
              {item.initiative
                ? `Iniciativa: ${item.initiative.name}`
                : item.objective
                  ? `Objetivo: ${item.objective.name}`
                  : 'Sem vínculo'}
            </dd>
            {item.description ? (
              <>
                <dt className="text-muted-foreground">Descrição</dt>
                <dd>{item.description}</dd>
              </>
            ) : null}
            <dt className="text-muted-foreground">Total geral (toda a vigência)</dt>
            <dd className="tabular">
              {money(item.total.planned)} planejado · {money(item.total.actual)} realizado ·{' '}
              {pct(item.total.consumedPercent)} consumido · saldo {money(item.total.available)}
            </dd>
          </dl>
          {item.archivedAt ? (
            <FormAlert tone="locked">Item arquivado: só consulta. Restaure-o para alterar.</FormAlert>
          ) : null}
          {yearsOfItem.length > 1 ? (
            <ItemYearPicker years={yearsOfItem} value={item.year} onChange={setItemYear} />
          ) : null}
          <MonthsEditor
            key={`${item.id}-${item.year}`}
            base={base}
            item={item}
            year={item.year}
            editable={canEdit && !item.archivedAt}
            onSaved={async (saved) => {
              queryClient.setQueryData(['budget', projectId, 'item', itemId, askedYear], saved);
              await onChanged();
            }}
          />
          <div className="flex flex-wrap justify-end gap-2">
            {canEdit && !item.archivedAt ? (
              <Button variant="outline" onClick={() => onEdit(item)}>
                <Pencil aria-hidden />
                Editar item
              </Button>
            ) : null}
            {canArchive ? (
              <Button
                variant="outline"
                loading={archiving}
                disabled={archiving}
                onClick={async () => {
                  await onArchive(item);
                  await query.refetch();
                }}
              >
                {item.archivedAt ? <ArchiveRestore aria-hidden /> : <Archive aria-hidden />}
                {item.archivedAt ? 'Restaurar item' : 'Arquivar item'}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

function ItemYearPicker({
  years,
  value,
  onChange,
}: {
  years: number[];
  value: number;
  onChange: (y: number) => void;
}) {
  const id = useId();
  return (
    <FormField id={id} label="Ano do detalhamento">
      <NativeSelect id={id} value={value} onChange={(e) => onChange(Number(e.target.value))}>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </NativeSelect>
    </FormField>
  );
}

/** Mês a mês do item no ano: planejado e realizado editáveis nos meses da vigência, com saldos e acumulados. */
function MonthsEditor({
  base,
  item,
  year,
  editable,
  onSaved,
}: {
  base: string;
  item: BudgetItem;
  year: number;
  editable: boolean;
  onSaved: (saved: BudgetItem) => Promise<void>;
}) {
  const { client } = useAuth();
  const ids = useId();
  const open = new Set(item.openMonths);
  const initial = () =>
    item.months.map((m) => ({ planned: inputText(m.planned), actual: inputText(m.actual) }));
  const [draft, setDraft] = useState(initial);
  const [reason, setReason] = useState('');
  const [needsReason, setNeedsReason] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  // Nova carga do item (outro conteúdo): o rascunho volta aos valores gravados. A mesma carga mantém a referência.
  useEffect(
    () => setDraft(item.months.map((m) => ({ planned: inputText(m.planned), actual: inputText(m.actual) }))),
    [item],
  );

  async function save(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    const months: { month: number; planned: number | null; actual: number | null }[] = [];
    for (const m of item.months) {
      if (!open.has(m.month)) continue;
      const d = draft[m.month - 1]!;
      const parse = (text: string, label: string) => {
        if (text.trim() === '') return { ok: true as const, value: null };
        const parsed = budgetValueSchema.safeParse(moneyInput(text));
        return parsed.success
          ? { ok: true as const, value: parsed.data }
          : {
              ok: false as const,
              message: `${label} de ${MONTHS[m.month - 1]}: ${parsed.error.issues[0]?.message}`,
            };
      };
      const planned = parse(d.planned, 'Planejado');
      const actual = parse(d.actual, 'Realizado');
      if (!planned.ok || !actual.ok) {
        setError(new Error(!planned.ok ? planned.message : (actual as { message: string }).message));
        return;
      }
      if (planned.value !== m.planned || actual.value !== m.actual) {
        months.push({ month: m.month, planned: planned.value, actual: actual.value });
      }
    }
    if (!months.length) return setError(new Error('Nenhum valor mudou.'));
    if (needsReason && !reason.trim()) return setError(new Error('Justificativa obrigatória.'));
    setSaving(true);
    setError(null);
    try {
      const saved = await client.request(`${base}/${item.id}/months`, {
        method: 'PUT',
        body: { year, months, ...(reason.trim() ? { reason: reason.trim() } : {}) },
        schema: budgetItemSchema,
      });
      toast.success('Valores do orçamento salvos', { description: `${item.name} · ${year}` });
      setNeedsReason(false);
      setReason('');
      await onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'CLOSED_PERIOD' || err.code === 'REASON_REQUIRED')) {
        setNeedsReason(true);
      }
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form noValidate onSubmit={save} className="flex flex-col gap-3">
      <div
        role="region"
        aria-label={`Mês a mês de ${year}`}
        tabIndex={0}
        className="relative overflow-x-auto rounded-lg border border-border"
      >
        <table className="w-full min-w-[44rem] border-collapse text-sm">
          <caption className="sr-only">
            {item.name}: planejado, realizado e saldos de {year}
          </caption>
          <thead className="bg-muted/60 text-xs text-muted-foreground">
            <tr>
              {['Mês', 'Planejado', 'Realizado', 'Saldo do mês', '% consumido', 'Saldo acumulado'].map(
                (h, i) => (
                  <th
                    key={h}
                    scope="col"
                    className={`px-3 py-2 font-semibold ${i ? 'text-right' : 'text-left'}`}
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {item.months.map((m) => {
              const inPeriod = open.has(m.month);
              const label = `${MONTHS[m.month - 1]}/${year}`;
              return (
                <tr
                  key={m.month}
                  className={`border-t border-border ${inPeriod ? '' : 'text-muted-foreground'}`}
                >
                  <th scope="row" className="px-3 py-2 text-left font-medium">
                    {label}
                    {inPeriod ? null : <span className="block text-xs font-normal">fora da vigência</span>}
                  </th>
                  {(['planned', 'actual'] as const).map((field) => (
                    <td key={field} className="px-3 py-2 text-right tabular">
                      {editable && inPeriod ? (
                        <Input
                          id={`${ids}-${field}-${m.month}`}
                          aria-label={`${field === 'planned' ? 'Planejado' : 'Realizado'} ${label}`}
                          inputMode="decimal"
                          disabled={saving}
                          value={draft[m.month - 1]?.[field] ?? ''}
                          onChange={(e) =>
                            setDraft((d) =>
                              d.map((v, i) => (i === m.month - 1 ? { ...v, [field]: e.target.value } : v)),
                            )
                          }
                          className="h-11 text-right tabular"
                        />
                      ) : (
                        money(m[field])
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular">{money(m.balance)}</td>
                  <td className="px-3 py-2 text-right tabular">{pct(m.consumedPercent)}</td>
                  <td
                    className={`px-3 py-2 text-right tabular ${m.balanceAccumulated < 0 ? 'text-status-red-fg' : ''}`}
                  >
                    {money(m.balanceAccumulated)}
                  </td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-border font-semibold">
              <th scope="row" className="px-3 py-2 text-left">
                Total do ano
              </th>
              <td className="px-3 py-2 text-right tabular">{money(item.annual.planned)}</td>
              <td className="px-3 py-2 text-right tabular">{money(item.annual.actual)}</td>
              <td className="px-3 py-2 text-right tabular">{money(item.annual.available)}</td>
              <td className="px-3 py-2 text-right tabular">{pct(item.annual.consumedPercent)}</td>
              <td className="px-3 py-2 text-right tabular">{money(item.annual.available)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {needsReason ? (
        <FormField id={`${ids}-reason`} label="Justificativa (obrigatória)">
          <textarea
            id={`${ids}-reason`}
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={textareaClass}
          />
        </FormField>
      ) : null}
      {error ? <FormAlert tone="error">{errorMessage(error)}</FormAlert> : null}
      {editable && open.size > 0 ? (
        <div className="flex justify-end">
          <Button type="submit" loading={saving}>
            Salvar valores
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function ItemForm({
  base,
  initial,
  categories,
  options,
  optionsError,
  onRetryOptions,
  onCancel,
  onDone,
}: {
  base: string;
  initial?: BudgetItem;
  categories: string[];
  options: BudgetOptions | undefined;
  optionsError: unknown;
  onRetryOptions: () => void;
  onCancel: () => void;
  onDone: (saved: BudgetItem) => Promise<void>;
}) {
  const { client } = useAuth();
  const ids = {
    name: useId(),
    description: useId(),
    category: useId(),
    categories: useId(),
    type: useId(),
    start: useId(),
    end: useId(),
    link: useId(),
    reason: useId(),
  };
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [category, setCategory] = useState(initial?.category ?? '');
  const [type, setType] = useState<BudgetTypeName>(initial?.type ?? 'OPEX');
  const [startDate, setStartDate] = useState(initial?.startDate ?? '');
  const [endDate, setEndDate] = useState(initial?.endDate ?? '');
  const [link, setLink] = useState(
    initial?.initiative
      ? `i:${initial.initiative.id}`
      : initial?.objective
        ? `o:${initial.objective.id}`
        : '',
  );
  const [reason, setReason] = useState('');
  const [needsReason, setNeedsReason] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  // O vínculo atual de um item pode ter sido arquivado depois (DEC-065): continua valendo e aparece na lista.
  const initialLink = initial?.initiative
    ? { value: `i:${initial.initiative.id}`, label: `Iniciativa: ${initial.initiative.name}` }
    : initial?.objective
      ? { value: `o:${initial.objective.id}`, label: `Objetivo: ${initial.objective.name}` }
      : null;
  const listed = (v: string) =>
    options
      ? [
          ...options.initiatives.map((i) => `i:${i.id}`),
          ...options.objectives.map((o) => `o:${o.id}`),
        ].includes(v)
      : true;
  const currentLink = initialLink && !listed(initialLink.value) ? initialLink : null;
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    const parsed = budgetItemInputSchema.safeParse({
      name,
      description: description.trim() || null,
      category,
      type,
      startDate,
      endDate,
      initiativeId: link.startsWith('i:') ? link.slice(2) : null,
      objectiveId: link.startsWith('o:') ? link.slice(2) : null,
    });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    if (needsReason && !reason.trim()) return setErrors({ reason: 'Justificativa obrigatória.' });
    setErrors({});
    setFormError(null);
    setSaving(true);
    try {
      const saved = await client.request(initial ? `${base}/${initial.id}` : base, {
        method: initial ? 'PUT' : 'POST',
        body: { ...parsed.data, ...(initial && reason.trim() ? { reason: reason.trim() } : {}) },
        schema: budgetItemSchema,
      });
      toast.success(initial ? 'Item salvo' : 'Item criado', { description: saved.name });
      await onDone(saved);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'REASON_REQUIRED') setNeedsReason(true);
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      setFormError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <FormField id={ids.name} label="Nome do item" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={160}
          placeholder="Ex.: Campanha de lançamento"
          onChange={(e) => setName(e.target.value)}
          className="h-11"
        />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.category} label="Categoria" error={errors.category}>
          <Input
            {...fieldA11y(ids.category, errors.category)}
            list={ids.categories}
            value={category}
            maxLength={80}
            placeholder="Ex.: Marketing"
            onChange={(e) => setCategory(e.target.value)}
            className="h-11"
          />
        </FormField>
        <datalist id={ids.categories}>
          {categories.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <FormField id={ids.type} label="Tipo" error={errors.type}>
          <NativeSelect
            {...fieldA11y(ids.type, errors.type)}
            value={type}
            onChange={(e) => setType(e.target.value as BudgetTypeName)}
          >
            {BUDGET_TYPES.map((t) => (
              <option key={t} value={t}>
                {BUDGET_TYPE_LABELS[t]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.start} label="Início da vigência" error={errors.startDate}>
          <Input
            {...fieldA11y(ids.start, errors.startDate)}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.end} label="Fim da vigência" error={errors.endDate}>
          <Input
            {...fieldA11y(ids.end, errors.endDate)}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-11"
          />
        </FormField>
      </div>
      <FormField id={ids.link} label="Vínculo (opcional)" error={errors.initiativeId ?? errors.objectiveId}>
        {optionsError ? (
          <ErrorState error={optionsError} onRetry={onRetryOptions} />
        ) : (
          <NativeSelect
            {...fieldA11y(ids.link, errors.initiativeId ?? errors.objectiveId)}
            value={link}
            disabled={!options}
            onChange={(e) => setLink(e.target.value)}
          >
            <option value="">{options ? 'Sem vínculo' : 'Carregando…'}</option>
            {currentLink ? (
              <option value={currentLink.value}>{currentLink.label} (arquivado — vínculo atual)</option>
            ) : null}
            {options?.initiatives.length ? (
              <optgroup label="Iniciativas">
                {options.initiatives.map((i) => (
                  <option key={i.id} value={`i:${i.id}`}>
                    {i.name}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {options?.objectives.length ? (
              <optgroup label="Objetivos">
                {options.objectives.map((o) => (
                  <option key={o.id} value={`o:${o.id}`}>
                    {o.name}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </NativeSelect>
        )}
      </FormField>
      <FormField id={ids.description} label="Descrição (opcional)" error={errors.description}>
        <textarea
          {...fieldA11y(ids.description, errors.description)}
          value={description}
          rows={2}
          maxLength={2000}
          onChange={(e) => setDescription(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {needsReason ? (
        <FormField id={ids.reason} label="Justificativa (obrigatória)" error={errors.reason}>
          <textarea
            {...fieldA11y(ids.reason, errors.reason)}
            value={reason}
            rows={2}
            maxLength={1000}
            onChange={(e) => setReason(e.target.value)}
            className={textareaClass}
          />
        </FormField>
      ) : null}
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar item' : 'Criar item'}
        </Button>
      </div>
    </form>
  );
}
